// Fit Score (v3) — shared definitions. One number, 0 to 100, for how well a
// government opportunity fits one company:
//
//   Fit Score = Gate Factor × Limit(Weighted Score + Learned Adjustment, 0, 100)
//
// Gate Factor = G1 × G2 × G3 × G4 (each 1, 0.5 or 0). Weighted Score = the
// eight criteria (C1–C8), each 0–100, weighted and averaged over the criteria
// that apply. Learned Adjustment = what this company's own bid / no-bid codes
// taught the system, held between −25 and +15.
//
// Money is in $ millions throughout (the same unit as an opportunity's
// estimated value). Dates are ISO strings (YYYY-MM-DD).
//
// The scoring engine lives in apps/server/src/fitScore.ts; these are the types,
// fixed lists and code tables both the server and the web app read.
export const FIT_CRITERIA = [
    { code: "C1", label: "Capability Match", weight: 30, asks: "Does the work match what we do, and is it our core work?" },
    { code: "C2", label: "Past Performance", weight: 20, asks: "Have we done this kind of work before, for this kind of customer, and done it well?" },
    { code: "C3", label: "Qualifications and Certifications", weight: 10, asks: "Do we hold the company and staff certifications the customer asks for?" },
    { code: "C4", label: "Security Posture", weight: 10, asks: "Do we hold the clearances and cybersecurity levels the work needs?" },
    { code: "C5", label: "Approved Business Systems", weight: 5, asks: "Are our accounting and other business systems approved for this type of contract?" },
    { code: "C6", label: "Competitive Position", weight: 10, asks: "Do we know this customer, did we help shape the need, and how crowded is the field?" },
    { code: "C7", label: "Size and Capacity", weight: 10, asks: "Is the job the right size for us, can we staff it, and can we work where it is?" },
    { code: "C8", label: "Strategic Alignment", weight: 5, asks: "Does it move us toward the customers and markets we want?" },
];
export const FIT_CRITERION_CODES = FIT_CRITERIA.map((c) => c.code);
export const FIT_START_WEIGHTS = Object.fromEntries(FIT_CRITERIA.map((c) => [c.code, c.weight]));
export const FIT_BANDS = [
    { band: "strong", label: "Strong Fit", min: 85, max: 100, action: "Top of the list, flagged for a pursue decision." },
    { band: "good", label: "Good Fit", min: 70, max: 84, action: "Main list. A bid decision is expected." },
    { band: "possible", label: "Possible Fit", min: 50, max: 69, action: "Main list, with a note to look at teaming." },
    { band: "weak", label: "Weak Fit", min: 30, max: 49, action: "Watch list only." },
    { band: "none", label: "No Fit", min: 0, max: 29, action: "Hidden by default. You can still search for it." },
];
export function fitBand(score) {
    return score >= 85 ? "strong" : score >= 70 ? "good" : score >= 50 ? "possible" : score >= 30 ? "weak" : "none";
}
export const FIT_BAND_LABEL = Object.fromEntries(FIT_BANDS.map((b) => [b.band, b.label]));
export const CAPABILITY_TIERS = [
    { tier: "core", label: "Core", value: 1.0, meaning: "Our main business. We have staff, past work and tools for it today." },
    { tier: "supporting", label: "Supporting", value: 0.6, meaning: "We do it well, usually alongside core work." },
    { tier: "emerging", label: "Emerging", value: 0.3, meaning: "We can do it and want to grow it, but have little past work." },
    { tier: "avoid", label: "Avoid", value: -1.0, meaning: "Work we choose not to do. It pulls the score down." },
];
export const TIER_VALUE = { core: 1.0, supporting: 0.6, emerging: 0.3, avoid: -1.0 };
export const SET_ASIDES = [
    { key: "none", label: "None (full and open)" },
    { key: "small_business", label: "Small business" },
    { key: "sdvosb", label: "Service disabled veteran owned small business" },
    { key: "vosb", label: "Veteran owned small business" },
    { key: "wosb", label: "Women owned small business" },
    { key: "edwosb", label: "Economically disadvantaged women owned small business" },
    { key: "hubzone", label: "HUBZone small business" },
    { key: "8a", label: "8(a) business development" },
    { key: "other", label: "Other set aside" },
];
export const CLEARANCE_RANK = { none: 0, confidential: 1, secret: 2, top_secret: 3 };
export const CLEARANCE_LABEL = { none: "None", confidential: "Confidential", secret: "Secret", top_secret: "Top Secret" };
export const CPARS_RATINGS = [
    { key: "exceptional", label: "Exceptional", factor: 1.0 },
    { key: "very_good", label: "Very Good", factor: 0.9 },
    { key: "satisfactory", label: "Satisfactory", factor: 0.8 },
    { key: "none", label: "No rating yet", factor: 0.75 },
    { key: "marginal", label: "Marginal", factor: 0.4 },
    { key: "unsatisfactory", label: "Unsatisfactory", factor: 0 },
];
export const BUSINESS_SYSTEMS = [
    { key: "accounting", label: "Accounting" },
    { key: "purchasing", label: "Purchasing" },
    { key: "estimating", label: "Estimating" },
    { key: "evm", label: "Earned value management" },
    { key: "property", label: "Property management" },
    { key: "material", label: "Material management and accounting" },
];
export const SYSTEM_STATUSES = [
    { key: "approved", label: "Approved or found adequate", score: 100 },
    { key: "review", label: "Review requested or scheduled", score: 60 },
    { key: "in_place", label: "In place, never reviewed", score: 40 },
    { key: "none", label: "No system in place", score: 0 },
];
export const CONTRACT_TYPES = [
    { key: "ffp", label: "Firm fixed price" },
    { key: "cost", label: "Cost reimbursement" },
    { key: "tm", label: "Time and materials" },
    { key: "other", label: "Other or mixed" },
];
export const DEFAULT_SIZE_BANDS = { tooSmall: 0.01, low: 0.05, high: 0.5, max: 1.0 };
export function emptyFitProfile() {
    return {
        setAsides: [], vehicles: [], partners: [],
        facilityClearance: "none", facilityStatus: "none", safeguarding: "none", safeguardingStatus: "none",
        clearedStaff: { secret: 0, top_secret: 0 }, cmmcLevel: 0, cmmcStatus: "none", nistScore: null,
        businessSystems: {}, credentials: [], pastPerformance: [],
        currentOffices: [], meetings: [], noticesAnswered: [],
        annualRevenue: null, staffAvailable: null, plannedHires: null, offices: [],
        targetCustomers: [],
        settings: { minDaysProposal: 10, minDaysRfi: 5, sizeBands: { ...DEFAULT_SIZE_BANDS }, lockedWeights: [] },
    };
}
export const IMPORTANCE_VALUE = { primary: 3, secondary: 2, minor: 1 };
export const PATTERN_TRAITS = [
    { trait: "agency", label: "Buying agency" },
    { trait: "contract_type", label: "Contract type" },
    { trait: "evaluation", label: "Evaluation method" },
    { trait: "set_aside", label: "Set aside type" },
    { trait: "vehicle", label: "Contract vehicle" },
    { trait: "value_band", label: "Value band" },
    { trait: "place", label: "Place of performance" },
    { trait: "work_category", label: "Work category" },
    { trait: "security_level", label: "Security level" },
];
export function isFitRecord(b) {
    return Boolean(b && typeof b === "object" && b.version === "fit-v3");
}
export const DECISION_CODES = [
    { code: "B", label: "Bid as prime contractor", kind: "bid" },
    { code: "S", label: "Bid as subcontractor", kind: "bid" },
    { code: "J", label: "Bid as a joint venture", kind: "bid" },
    { code: "N", label: "No bid", kind: "no_bid" },
    { code: "R", label: "Refer to a partner (fits a partner better than us)", kind: "no_bid" },
    { code: "W", label: "Watch and decide later", kind: "watch" },
];
export const decisionKind = (d) => DECISION_CODES.find((x) => x.code === d).kind;
export const SOURCE_CODES = [
    { code: "A", label: "Automated scan" },
    { code: "C", label: "Customer told us" },
    { code: "T", label: "Teaming partner referral" },
    { code: "E", label: "Industry day, conference, or other event" },
    { code: "F", label: "Agency buying forecast" },
    { code: "O", label: "Other" },
];
export const BID_REASONS = [
    { code: "101", label: "Core work, strong capability match", learning: "fit" },
    { code: "102", label: "We are the incumbent, or this replaces our own contract", learning: "fit" },
    { code: "103", label: "We have a working relationship with this buying office", learning: "fit" },
    { code: "104", label: "We helped shape this need (answered an RFI, attended an industry day, sent a white paper)", learning: "fit" },
    { code: "105", label: "Strategic entry into a target customer or market", learning: "strategic" },
    { code: "106", label: "Our past performance is directly on point", learning: "fit" },
    { code: "107", label: "The set aside favors us", learning: "fit" },
    { code: "108", label: "We hold the required contract vehicle", learning: "fit" },
    { code: "109", label: "A strong teaming partner brought us in", learning: "strategic" },
    { code: "110", label: "We have a price advantage", learning: "strategic" },
    { code: "111", label: "We have a unique technology or product that sets us apart", learning: "fit" },
    { code: "112", label: "Leads to larger follow on work", learning: "strategic" },
    { code: "113", label: "We have staff free to do the work now", learning: "situational" },
    { code: "114", label: "Leadership direction", learning: "situational" },
    { code: "199", label: "Other (comment required)", learning: "situational" },
];
export const NO_BID_REASONS = [
    { code: "201", label: "Not our core work", series: 200, learns: "Lowers Keyword Confidence for the marked requirement areas. Asks whether to change their tier." },
    { code: "202", label: "Work we choose not to do", series: 200, learns: "Asks whether to move the marked areas to the Avoid tier." },
    { code: "203", label: "Our past performance is too weak or not relevant", series: 200, learns: "Lowers the Work category pattern. Adds the area to the past performance gap list.", pattern: "work_category" },
    { code: "204", label: "Missing a required certification or qualification", series: 200, learns: "Lowers the Work category pattern. Adds the credential to the gap list.", pattern: "work_category" },
    { code: "205", label: "Missing a required clearance or cybersecurity level", series: 200, learns: "Lowers the Security level pattern. Adds the item to the gap list.", pattern: "security_level" },
    { code: "206", label: "Missing a required approved business system", series: 200, learns: "Lowers the Contract type pattern. Adds the system to the gap list.", pattern: "contract_type" },
    { code: "207", label: "Not eligible for the set aside", series: 200, learns: "Lowers the Set aside type pattern.", pattern: "set_aside" },
    { code: "208", label: "Not on the required contract vehicle, with no partner path", series: 200, learns: "Lowers the Contract vehicle pattern.", pattern: "vehicle" },
    { code: "209", label: "Too large for us", series: 200, learns: "Lowers the Value band pattern.", pattern: "value_band" },
    { code: "210", label: "Too small to be worth the cost of bidding", series: 200, learns: "Lowers the Value band pattern.", pattern: "value_band" },
    { code: "211", label: "A contract type we avoid", series: 200, learns: "Lowers the Contract type pattern.", pattern: "contract_type" },
    { code: "212", label: "Place of performance does not work for us", series: 200, learns: "Lowers the Place of performance pattern.", pattern: "place" },
    { code: "213", label: "A customer we do not pursue", series: 200, learns: "Lowers the Buying agency pattern.", pattern: "agency" },
    { code: "214", label: "Lowest price technically acceptable, and we cannot win on price", series: 200, learns: "Lowers the Evaluation method pattern.", pattern: "evaluation" },
    { code: "290", label: "Agreed with the low score (filled in for us below a score of 50)", series: 200, learns: "No pattern change. Counts in weight tuning." },
    { code: "299", label: "Other fit reason (comment required)", series: 200, learns: "No automatic change. Reviewed each month and remapped to a code if a trend shows up." },
    { code: "301", label: "Proposal team is at capacity", series: 300, learns: "Logged only." },
    { code: "302", label: "Due date too soon", series: 300, learns: "Logged only." },
    { code: "303", label: "Key staff not available", series: 300, learns: "Logged only." },
    { code: "304", label: "Teaming partner fell through", series: 300, learns: "Logged only." },
    { code: "305", label: "Conflict of interest", series: 300, learns: "Logged only." },
    { code: "306", label: "We would compete against a partner", series: 300, learns: "Logged only." },
    { code: "307", label: "Bid and proposal budget is spent", series: 300, learns: "Logged only." },
    { code: "308", label: "Waiting on a change or the final notice", series: 300, learns: "Logged only." },
    { code: "309", label: "Strong incumbent, low chance to win this time", series: 300, learns: "Logged only." },
    { code: "310", label: "Appears written for another company", series: 300, learns: "Logged only." },
    { code: "311", label: "Leadership direction", series: 300, learns: "Logged only." },
    { code: "399", label: "Other situational reason (comment required)", series: 300, learns: "Logged only." },
    { code: "401", label: "A keyword matched, but it meant something else", series: 400, learns: "Lowers Keyword Confidence by 0.20 for the marked keyword and saves the wrong meaning." },
    { code: "402", label: "Already awarded or canceled", series: 400, learns: "Removes it from the list and flags the source." },
    { code: "403", label: "Duplicate notice", series: 400, learns: "Removes it from the list as a duplicate." },
    { code: "404", label: "Our company profile is out of date", series: 400, learns: "Opens the profile for update, then scores again." },
    { code: "405", label: "The notice data was read wrong (value, dates, set aside)", series: 400, learns: "Opens the opportunity for correction, then scores again." },
    { code: "499", label: "Other data error (comment required)", series: 400, learns: "Sent to the administrator." },
];
export const OUTCOME_CODES = [
    { code: "501", label: "Won" },
    { code: "502", label: "Lost on price" },
    { code: "503", label: "Lost on technical approach" },
    { code: "504", label: "Lost on past performance" },
    { code: "505", label: "Canceled by the government" },
    { code: "506", label: "Lost, reason not given" },
    { code: "599", label: "Other (comment required)" },
];
export const reasonLabel = (code) => BID_REASONS.find((r) => r.code === code)?.label ?? NO_BID_REASONS.find((r) => r.code === code)?.label ?? OUTCOME_CODES.find((r) => r.code === code)?.label ?? code;
// Which score a decision is judged against: B (bid as prime) uses the solo
// score, S and J (sub or joint venture with the prime) use the team score,
// and N, R and W use the headline (higher) score.
export function decisionPath(d, headline) {
    return d === "B" ? "solo" : d === "S" || d === "J" ? "prime" : headline;
}
